import { useState, useCallback } from "react";
import {
  generateHoroscope,
  generateHoroscopeWithProfile,
  getCachedHoroscope,
  cacheHoroscope,
} from "../utils/gemini";
import { getZodiacSign } from "../utils/zodiac";
import { isRateLimitError, incrementUsage } from "../utils/rateLimitUtils";

/**
 * Custom hook for generating horoscopes
 * Handles loading state, caching, and error handling
 */
export const useHoroscope = () => {
  const [horoscope, setHoroscope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [rateLimitRemaining, setRateLimitRemaining] = useState(null);
  const [isApiRateLimit, setIsApiRateLimit] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const generateHoroscopeForSign = useCallback(
    async (dateOfBirth, timeframe = "TODAY", name = "", profession = "Just a Human 🫠") => {
      setLoading(true);
      setError(null);
      setIsApiRateLimit(false);

      try {
        const zodiacSign = getZodiacSign(new Date(dateOfBirth));
        const cacheKey = `horoscope_${zodiacSign.name}_${timeframe}_${name}_${profession}_${new Date(dateOfBirth)
          .toISOString()
          .split("T")[0]}`;

        // Check cache first
        const cached = getCachedHoroscope(cacheKey);
        if (cached) {
          setHoroscope({ ...cached, zodiacSign, name, profession });
          setLoading(false);
          incrementUsage();
          return { ...cached, zodiacSign, name, profession };
        }

        // Generate new horoscope with profile
        const newHoroscope = await generateHoroscopeWithProfile(
          name || "bestie",
          zodiacSign.name,
          profession === "Just a Human 🫠" ? "general" : profession,
          timeframe
        );

        // Cache the result
        cacheHoroscope(cacheKey, newHoroscope);

        const result = { ...newHoroscope, zodiacSign, name, profession };
        setHoroscope(result);
        setLoading(false);
        setRetryCount(0);

        // Increment usage after successful generation
        incrementUsage();

        return result;
      } catch (err) {
        // Check if it's a rate limit error from Gemini API
        if (isRateLimitError(err)) {
          setIsApiRateLimit(true);
          setError("RATE_LIMIT_ERROR");
          setLoading(false);
          setRetryCount((prev) => prev + 1);
          throw err;
        }

        // Generic error handling
        const errorMessage = name 
          ? `Hang on ${name}, the stars are buffering... 🌟`
          : "The stars are drunk right now. Try again in a moment 🌟";
        setError(errorMessage);
        setIsApiRateLimit(false);
        setLoading(false);
        throw err;
      }
    },
    []
  );

  const reset = useCallback(() => {
    setHoroscope(null);
    setError(null);
    setLoading(false);
    setIsApiRateLimit(false);
    setRetryCount(0);
  }, []);

  return {
    horoscope,
    loading,
    error,
    generateHoroscopeForSign,
    reset,
    rateLimitRemaining,
    isApiRateLimit,
    retryCount,
  };
};
